// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import {
    ApiHandler,
    createHandlerChainRunner,
    HasOnSuccess,
    markSkipOnCompleteIfOnSuccessIsSet,
} from "@insite/client-framework/HandlerCreator";
import {
    getOpenBackOrders,
    OpenBackOrdersApiParameter,
    OpenBackOrdersResultModel,
} from "@steiner/App/Services/OpenBackOrders";

type HandlerType = ApiHandler<OpenBackOrdersApiParameter & HasOnSuccess, OpenBackOrdersResultModel>;

export const DispatchBeginLoadOpenBackOrders: HandlerType = props => {
    props.dispatch({
        type: "xOpenBackOrders/BeginLoadingOpenBackOrders",
        isLoading: true,
    } as any);
};

export const PopulateApiParameters: HandlerType = props => {
    props.apiParameter = {
        ...props.parameter,
    };
};

export const DispatchUpdateLoadOpenBackOrdersParameter: HandlerType = props => {
    props.dispatch({
        type: "xOpenBackOrders/UpdateLoadOpenBackOrdersParameter",
        value: props.parameter,
    } as any);
};

export const RequestDataFromApi: HandlerType = async props => {
    try {
        props.apiResult = await getOpenBackOrders(props.apiParameter);
    } catch (error) {
        props.dispatch({
            type: "xOpenBackOrders/FailedLoadingOpenBackOrders",
        } as any);
        return false;
    }
};

/**
 * Copied from classic
 * BE doesn't return the full pagination model instead
 * they return totalItemCount, pageNumber, pageNumber, pageSize & defaultPageSize only
 * so we need to build the pagination model to use the Pagination and TableCount components
 * @param props
 */
export const PreparePagination: HandlerType = props => {
    props.apiResult.pagination = {
        totalItemCount: props.apiResult.totalItemCount,
        page: props.apiResult.pageNumber,
        currentPage: props.apiResult.pageNumber,
        pageSize: props.apiResult.pageSize,
        defaultPageSize: props.apiResult.defaultPageSize,
        numberOfPages: Math.ceil(props.apiResult.totalItemCount / props.apiResult.pageSize),
        pageSizeOptions: [
            props.apiResult.defaultPageSize,
            props.apiResult.defaultPageSize * 2,
            props.apiResult.defaultPageSize * 3,
            props.apiResult.defaultPageSize * 4,
        ],
        sortOptions: [],
        sortType: "",
        nextPageUri: "",
        prevPageUri: "",
    };
};

export const DispatchCompleteLoadOpenBackOrders: HandlerType = props => {
    props.dispatch({
        type: "xOpenBackOrders/CompleteLoadingOpenBackOrders",
        value: props.apiResult,
    } as any);
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    markSkipOnCompleteIfOnSuccessIsSet(props);
    props.parameter.onSuccess?.();
};

export const chain = [
    DispatchBeginLoadOpenBackOrders,
    PopulateApiParameters,
    DispatchUpdateLoadOpenBackOrdersParameter,
    RequestDataFromApi,
    PreparePagination,
    DispatchCompleteLoadOpenBackOrders,
    ExecuteOnSuccessCallback,
];

const loadOpenBackOrders = createHandlerChainRunner(chain, "loadOpenBackOrders");

export default loadOpenBackOrders;
